/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { Dispatch, StateUpdater } from 'preact/hooks';
import type { ActiveCell, ColumnHeaderFooterInfo } from '../Table';

import { getCellType } from '#PRIVATE_TableCommon/utils/DomUtils';
import { getColumnKey, TABLE_FOCUSABLE_SELECTOR } from './TableDomUtils';

/**
 * Helper function to handle a hover gesture over a column header or footer region.
 */
export function handleOverColumnHeaderFooterGesture<C>(
  tableId: string,
  event: PointerEvent,
  setHoverStyleColumnInfo: Dispatch<StateUpdater<ColumnHeaderFooterInfo<C> | undefined>>,
  hoverStyleColumnInfo?: ColumnHeaderFooterInfo<C>
) {
  // only track 'hover' information for mouse events
  if (event.pointerType === 'mouse') {
    const cellElement = (event.target as HTMLElement).closest(
      `[${TABLE_FOCUSABLE_SELECTOR}='${tableId}']`
    ) as HTMLElement;
    if (cellElement != null) {
      const cellType = getCellType(cellElement);
      if (cellType === 'header' || cellType === 'footer') {
        const isHeader = cellType === 'header';
        const columnKey = getColumnKey<C>(cellElement);
        if (
          columnKey !== hoverStyleColumnInfo?.key ||
          !hoverStyleColumnInfo?.isHeader !== isHeader
        ) {
          setHoverStyleColumnInfo({ key: columnKey, isHeader: cellType === 'header' });
          return;
        }
      }
    }
  }
  handleEndInteractionColumnHeaderFooterGesture(setHoverStyleColumnInfo, hoverStyleColumnInfo);
}

/**
 * Helper function to handle an active gesture over a column header or footer region.
 */
export function handleActiveColumnHeaderFooterGesture<K, C>(
  tableId: string,
  event: KeyboardEvent | PointerEvent,
  setActiveStyleColumnInfo: Dispatch<StateUpdater<ColumnHeaderFooterInfo<C> | undefined>>,
  activeStyleColumnInfo?: ColumnHeaderFooterInfo<C>,
  activeCell?: ActiveCell<K, C>
) {
  let cellType;
  let columnKey;
  if (activeCell != null) {
    cellType = activeCell.type;
    columnKey = activeCell.columnKey;
  } else if (event instanceof PointerEvent && event.pointerType === 'touch') {
    // only handle touch events since hover events have the same treatment
    const cellElement = (event.target as HTMLElement).closest(
      `[${TABLE_FOCUSABLE_SELECTOR}='${tableId}']`
    ) as HTMLElement;
    if (cellElement != null) {
      cellType = getCellType(cellElement);
      columnKey = getColumnKey<C>(cellElement);
    }
  }
  if (columnKey != null && (cellType === 'header' || cellType === 'footer')) {
    const isHeader = cellType === 'header';
    if (columnKey !== activeStyleColumnInfo?.key || !activeStyleColumnInfo?.isHeader !== isHeader) {
      setActiveStyleColumnInfo({ key: columnKey, isHeader: cellType === 'header' });
      return;
    }
  }
  handleEndInteractionColumnHeaderFooterGesture(setActiveStyleColumnInfo, activeStyleColumnInfo);
}

/**
 * Helper function to handle the end of an interaction style gesture for the table region.
 */
export function handleEndInteractionColumnHeaderFooterGesture<C>(
  setInteractionStyleColumnInfo: Dispatch<StateUpdater<ColumnHeaderFooterInfo<C> | undefined>>,
  interactionStyleColumnInfo?: ColumnHeaderFooterInfo<C>
) {
  if (interactionStyleColumnInfo != null) {
    setInteractionStyleColumnInfo(undefined);
  }
}
